import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, Typography, Box, Card, CardContent, CardActionArea,
  Chip, CircularProgress, Alert, Button, Divider, Badge, IconButton,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  Add as AddIcon, Circle as CircleIcon, Refresh as RefreshIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatDateDisplay } from '../utils/dateFormatter';
import supportService from '../services/supportService';

const STATUS_COLORS = {
  open: 'info',
  in_progress: 'warning',
  waiting_user: 'error',
  waiting_agent: 'default',
  resolved: 'success',
  closed: 'default'
};

const SupportTicketsPage = () => {
  const { t } = useTranslation(['support', 'common']);
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [sortBy, setSortBy] = useState('createdAt-desc');

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const res = await supportService.getMyTickets();
      if (res.success) setTickets(res.data?.tickets || res.data || []);
    } catch {
      setError(t('support:errors.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Filter and sort tickets
  const filteredAndSortedTickets = useMemo(() => {
    let filtered = tickets;

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(ticket => ticket.status === statusFilter);
    }

    // Apply priority filter
    if (priorityFilter) {
      filtered = filtered.filter(ticket => ticket.priority === priorityFilter);
    }

    // Apply sorting
    const [sortField, sortDirection] = sortBy.split('-');
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      let comparison = 0;
      if (aVal < bVal) comparison = -1;
      if (aVal > bVal) comparison = 1;

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [tickets, statusFilter, priorityFilter, sortBy]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 3,
          py: 2,
          px: 3,
          borderRadius: 2,
          background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
          color: 'white',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
              {t('support:ticket.title')}
            </Typography>
            <Typography variant="subtitle1" sx={{ mb: 0, color: 'white' }}>
              {t('support:ticket.titleSubtitle', 'Gérez vos demandes d\'assistance')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/support/new')}
            sx={{ bgcolor: 'white', color: '#6200ee', fontWeight: 600, whiteSpace: 'nowrap', ml: 2 }}
          >
            {t('support:ticket.create')}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters and Sorting */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="status-filter-label">{t('support:ticket.status') || 'Status'}</InputLabel>
            <Select
              labelId="status-filter-label"
              value={statusFilter}
              label={t('support:ticket.status') || 'Status'}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">{t('common:all') || 'All'}</MenuItem>
              <MenuItem value="open">{t('support:status.open') || 'Open'}</MenuItem>
              <MenuItem value="in_progress">{t('support:status.in_progress') || 'In Progress'}</MenuItem>
              <MenuItem value="waiting_user">{t('support:status.waiting_user') || 'Waiting User'}</MenuItem>
              <MenuItem value="waiting_agent">{t('support:status.waiting_agent') || 'Waiting Agent'}</MenuItem>
              <MenuItem value="resolved">{t('support:status.resolved') || 'Resolved'}</MenuItem>
              <MenuItem value="closed">{t('support:status.closed') || 'Closed'}</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="priority-filter-label">{t('support:ticket.priority') || 'Priority'}</InputLabel>
            <Select
              labelId="priority-filter-label"
              value={priorityFilter}
              label={t('support:ticket.priority') || 'Priority'}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <MenuItem value="">{t('common:all') || 'All'}</MenuItem>
              <MenuItem value="low">{t('support:priority.low') || 'Low'}</MenuItem>
              <MenuItem value="medium">{t('support:priority.medium') || 'Medium'}</MenuItem>
              <MenuItem value="high">{t('support:priority.high') || 'High'}</MenuItem>
              <MenuItem value="urgent">{t('support:priority.urgent') || 'Urgent'}</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="sort-label">{t('common:sortBy') || 'Sort By'}</InputLabel>
            <Select
              labelId="sort-label"
              value={sortBy}
              label={t('common:sortBy') || 'Sort By'}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="createdAt-desc">{t('common:newest') || 'Newest'}</MenuItem>
              <MenuItem value="createdAt-asc">{t('common:oldest') || 'Oldest'}</MenuItem>
              <MenuItem value="priority-desc">{t('support:priority.highest') || 'Highest Priority'}</MenuItem>
              <MenuItem value="priority-asc">{t('support:priority.lowest') || 'Lowest Priority'}</MenuItem>
              <MenuItem value="updatedAt-desc">{t('common:recentlyUpdated') || 'Recently Updated'}</MenuItem>
            </Select>
          </FormControl>

          <IconButton
            onClick={fetchTickets}
            size="small"
            sx={{
              ml: 'auto',
              color: 'inherit',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
            }}
            title="Refresh"
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filteredAndSortedTickets.length === 0 ? (
        <Alert severity="info">{t('support:ticket.noTickets')}</Alert>
      ) : (
        filteredAndSortedTickets.map(ticket => (
          <Card key={ticket.id} sx={{ mb: 2, cursor: 'pointer' }}>
            <CardActionArea onClick={() => window.open(`/support/tickets/${ticket.id}`, '_blank')}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('support:ticket.ticketNumber', { number: ticket.ticketNumber })}
                    </Typography>
                    <Chip
                      label={t(`support:status.${ticket.status}`)}
                      color={STATUS_COLORS[ticket.status] || 'default'}
                      size="small"
                    />
                    {ticket.priority === 'urgent' && (
                      <Chip label={t('support:priority.urgent')} color="error" size="small" variant="outlined" />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight={500}>
                      {ticket.subject}
                    </Typography>
                    {ticket.unreadMessageCount > 0 && (
                      <Badge badgeContent={ticket.unreadMessageCount} color="error">
                        <Box />
                      </Badge>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('support:ticket.createdAt', { date: formatDateDisplay(ticket.createdAt) })}
                    {ticket.updatedAt && ` — ${t('support:ticket.lastUpdate', { date: formatDateDisplay(ticket.updatedAt) })}`}
                  </Typography>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        ))
      )}
    </Container>
  );
};

export default SupportTicketsPage;
